/**
 * TrustGrid Module Sidebar
 * Secondary navigation for TrustGrid module
 * Positioned to the right of primary sidebar (60px)
 * Always expanded (250px) when shown
 * Auto-closes when menu item clicked
 */

import React, { useState, useEffect } from 'react';
import { Layout, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  SafetyOutlined,
  ProjectOutlined,
  ApiOutlined,
  ScanOutlined,
  ControlOutlined,
  FileSearchOutlined,
  WarningOutlined,
  FolderOutlined,
  BellOutlined,
  BarChartOutlined,
  SettingOutlined,
  AuditOutlined,
  BugOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  AppstoreOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { spacing, colors } from '../../theme/designTokens';

const { Sider } = Layout;

/**
 * Navigation menu structure
 * Organized into logical sections
 */
const getMenuItems = () => [
  {
    key: 'overview',
    label: 'Overview',
    type: 'group',
    children: [
      {
        key: '/trustgrid/msp-dashboard',
        icon: <TeamOutlined />,
        label: 'MSP Dashboard',
      },
      {
        key: '/trustgrid/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    type: 'group',
    children: [
      {
        key: '/trustgrid/compliance',
        icon: <SafetyOutlined />,
        label: 'Compliance',
      },
      {
        key: '/trustgrid/control-results',
        icon: <CheckCircleOutlined />,
        label: 'Control Results',
      },
    ],
  },
  {
    key: 'findings',
    label: 'Findings',
    type: 'group',
    children: [
      {
        key: '/trustgrid/findings/summary',
        icon: <WarningOutlined />,
        label: 'Summary',
      },
      {
        key: '/trustgrid/findings/details',
        icon: <FileSearchOutlined />,
        label: 'Details',
      },
      {
        key: '/trustgrid/findings/detection-plan',
        icon: <BookOutlined />,
        label: 'Detection Plan',
      },
      {
        key: '/trustgrid/scans',
        icon: <ScanOutlined />,
        label: 'Scans',
      },
      {
        key: '/trustgrid/old-findings',
        icon: <WarningOutlined />,
        label: 'Old Findings',
      },
    ],
  },
  {
    key: 'risk',
    label: 'Risk Management',
    type: 'group',
    children: [
      {
        key: '/trustgrid/cases',
        icon: <FolderOutlined />,
        label: 'Cases',
      },
      {
        key: '/trustgrid/alerts',
        icon: <BellOutlined />,
        label: 'Alerts',
      },
    ],
  },
  {
    key: 'configuration',
    label: 'Configuration',
    type: 'group',
    children: [
      {
        key: '/trustgrid/projects',
        icon: <ProjectOutlined />,
        label: 'Projects',
      },
      {
        key: '/trustgrid/connectors',
        icon: <ApiOutlined />,
        label: 'Connectors',
      },
      {
        key: '/trustgrid/controls',
        icon: <ControlOutlined />,
        label: 'Controls',
      },
      {
        key: '/trustgrid/settings',
        icon: <SettingOutlined />,
        label: 'Settings',
      },
    ],
  },
  {
    key: 'data',
    label: 'Data',
    type: 'group',
    children: [
      {
        key: '/trustgrid/evidence',
        icon: <FileSearchOutlined />,
        label: 'Evidence',
      },
    ],
  },
  {
    key: 'reporting',
    label: 'Reporting',
    type: 'group',
    children: [
      {
        key: '/trustgrid/reports',
        icon: <BarChartOutlined />,
        label: 'Reports',
      },
    ],
  },
  {
    key: 'system',
    label: 'System',
    type: 'group',
    children: [
      {
        key: '/trustgrid/audit-trail',
        icon: <AuditOutlined />,
        label: 'Audit Trail',
      },
      {
        key: '/trustgrid/diagnostics',
        icon: <BugOutlined />,
        label: 'Diagnostics',
      },
    ],
  },
  {
    key: 'development',
    label: 'Development',
    type: 'group',
    children: [
      {
        key: '/trustgrid/showcase',
        icon: <AppstoreOutlined />,
        label: 'Component Showcase',
      },
    ],
  },
];

function TrustGridSidebar({ onClose, primaryExpanded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Responsive behavior: detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current selected key from location
  const selectedKey = location.pathname;

  const handleMenuClick = ({ key }) => {
    navigate(key);
    // Auto-close sidebar after navigation
    if (onClose) {
      onClose();
    }
  };

  // Overlay mode on small screens
  const isOverlayMode = isSmallScreen;

  return (
    <>
      {/* Backdrop for overlay mode on small screens */}
      {isOverlayMode && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.24s',
          }}
        />
      )}

      {/* Click area above sidebar to close */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          left: primaryExpanded ? 200 : 56,
          top: 0,
          width: 240,
          height: '207px',
          zIndex: 40,
          cursor: 'pointer',
        }}
      />

      <Sider
        width={240}
        trigger={null}
        style={{
          height: 'calc(100vh - 207px)',
          position: 'fixed',
          left: isOverlayMode ? 0 : (primaryExpanded ? 200 : 56),
          top: '207px',
          bottom: 0,
          boxShadow: isOverlayMode ? '4px 0 16px rgba(0, 0, 0, 0.3)' : 'none',
          zIndex: isOverlayMode ? 1001 : 40,
          backgroundColor: colors.sidebar.secondary,
          borderRight: `1px solid ${colors.sidebar.divider}`,
          transition: 'left 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
        theme="dark"
      >
      {/* Scrollable content area */}
      <div
        className="trustgrid-sidebar-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          paddingTop: '16px',
          paddingBottom: '16px',
        }}
      >
        {/* Navigation menu */}
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          onClick={handleMenuClick}
          items={getMenuItems()}
          theme="dark"
          style={{
            borderRight: 0,
            paddingTop: 0,
            paddingBottom: spacing.xxl,
            paddingLeft: '16px',
            backgroundColor: 'transparent',
            fontSize: 14,
          }}
          className="trustgrid-panel-menu"
        />
      </div>
    </Sider>
    </>
  );
}

export default TrustGridSidebar;
